<template>
  <div class="cc-page">
    <!-- 代码简介 -->
    <section class="intro-section">
      <div class="intro-card">
        <h2>代码简介</h2>
        <div class="intro-content">
          <p>ISO 3166-1 提供了国家代码(Country codes)的三种表现方式：</p>
          <ul>
            <li><strong>Alpha-2（二位字母码，也称 二字码）</strong>：最常用，也是互联网顶级域名（ccTLD）基础。</li>
            <li><strong>Alpha-3（三位字母码，也称 三字码）</strong>：与国名关联更直观，便于人识别。</li>
            <li><strong>Numeric（三位数字码，也称 数字码）</strong>：与联合国统计司一致，避开拉丁字母问题。</li>
          </ul>
          <p class="tips">
             <strong>使用提示</strong>：🔍 快速查找：点击 A-Z 字母跳转 · 点击分组展开/折叠 &nbsp;&nbsp;📱手机端：左右滑动浏览
          </p>
        </div>
      </div>
    </section>

    <!-- A-Z 索引 -->
    <nav class="alpha-nav">
      <div class="alpha-nav-inner">
        <a
          v-for="letter in allLetters"
          :key="letter"
          :href="'#group-' + letter"
          class="alpha-link"
          @click.prevent="scrollToGroup(letter)"
        >{{ letter }}</a>
        <span class="alpha-count">共 {{ totalCount }} 条</span>
      </div>
    </nav>

    <!-- 数据列表 -->
    <main class="data-section">
      <div
        v-for="letter in allLetters"
        :key="letter"
        :id="'group-' + letter"
        class="letter-group"
      >
        <div
          class="group-header"
          :class="{ expanded: expandedGroups[letter] }"
          @click="toggleGroup(letter)"
        >
          <span class="group-toggle">{{ expandedGroups[letter] ? '▾' : '▸' }}</span>
          <span class="group-letter">{{ letter }}</span>
          <span class="group-count">（{{ groupedData[letter]?.length || 0 }}条）</span>
        </div>
        <div class="group-body" v-show="expandedGroups[letter]">
          <div class="table-wrapper">
            <table class="data-table">
              <thead>
                <tr>
                  <th class="col-sn">SN</th>
                  <th class="col-zh">中文简称</th>
                  <th class="col-en">英文简称</th>
                  <th class="col-iso">二字码</th>
                  <th class="col-iso">三字码</th>
                  <th class="col-num">数字码</th>
                  <th class="col-code">电话区号</th>
                  <th class="col-domain">域名后缀</th>
                  <th class="col-remark">备注</th>
                  <th class="col-enRemark">英文备注</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="item in groupedData[letter]"
                  :key="item.iso3"
                  :class="{ 'row-even': item.sn % 2 === 0 }"
                >
                  <td class="col-sn">{{ item.sn }}</td>
                  <td class="col-zh">{{ item.zh }}</td>
                  <td class="col-en">{{ item.en }}</td>
                  <td class="col-iso"><code>{{ item.iso2 }}</code></td>
                  <td class="col-iso"><code>{{ item.iso3 }}</code></td>
                  <td class="col-num">{{ item.num }}</td>
                  <td class="col-code">{{ item.code }}</td>
                  <td class="col-domain">{{ item.domain }}</td>
                  <td class="col-remark">{{ item.remark }}</td>
                  <td class="col-enRemark">{{ item.engRemark }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import countries, { getGroupedData, getAllLetters } from '../data/countries.js'

export default {
  name: 'CountryCodePage',
  data() {
    return {
      groupedData: getGroupedData(),
      allLetters: getAllLetters(),
      totalCount: countries.length,
      expandedGroups: {}
    }
  },
  mounted() {
    this.allLetters.forEach(letter => {
      this.expandedGroups[letter] = true
    })
  },
  methods: {
    toggleGroup(letter) {
      this.expandedGroups[letter] = !this.expandedGroups[letter]
    },
    scrollToGroup(letter) {
      if (!this.expandedGroups[letter]) {
        this.expandedGroups[letter] = true
      }
      this.$nextTick(() => {
        const el = document.getElementById('group-' + letter)
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
    }
  }
}
</script>

<style scoped>
.cc-page { max-width: 1100px; margin: 0 auto; padding: 0 16px 30px; }

.intro-section { margin-top: 20px; }
.intro-card {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
}
.intro-card h2 { font-size: 18px; color: #1a1a2e; margin-bottom: 14px; }
.intro-content ul { margin: 10px 0 10px 20px; }
.intro-content li { margin: 6px 0; font-size: 14px; color: #555; }
.intro-content .tips {
  margin-top: 14px;
  padding: 12px 16px;
  background: #f0f5ff;
  border-radius: 8px;
  font-size: 13px;
  color: #1677ff;
  border-left: 3px solid #1677ff;
}
kbd { background: #e8e8e8; border: 1px solid #ccc; border-radius: 3px; padding: 1px 6px; font-size: 12px; font-family: monospace; }

.alpha-nav {
  position: sticky;
  top: 48px;
  z-index: 100;
  background: #f5f7fa;
  padding-top: 4px;
  padding-bottom: 8px;
}
.alpha-nav-inner {
  background: #fff;
  border-radius: 10px;
  padding: 12px 16px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}
.alpha-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px; height: 32px;
  border-radius: 6px;
  font-size: 14px; font-weight: 600; color: #555;
  transition: all 0.2s;
  text-decoration: none;
}
.alpha-link:hover { background: #1677ff; color: #fff; transform: translateY(-1px); }
.alpha-count { margin-left: auto; font-size: 12px; color: #999; }

.data-section { margin-top: 12px; }

.letter-group {
  margin-top: 12px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 1px 6px rgba(0,0,0,0.05);
  overflow: hidden;
  scroll-margin-top: 120px;
}

.group-header {
  display: flex;
  align-items: center;
  padding: 14px 20px;
  cursor: pointer;
  user-select: none;
  background: #fafbfc;
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.2s;
}
.group-header:hover { background: #f0f5ff; }
.group-header.expanded { background: #e6f4ff; }
.group-toggle { width: 20px; font-size: 14px; color: #1677ff; }
.group-letter { font-size: 20px; font-weight: 700; color: #1a1a2e; margin: 0 8px; }
.group-count { font-size: 13px; color: #999; }

.table-wrapper { overflow-x: auto; }
.data-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.data-table thead { background: #fafbfc; position: sticky; top: 0; }
.data-table th {
  padding: 10px 8px;
  text-align: left;
  font-weight: 600;
  color: #555;
  border-bottom: 2px solid #e8e8e8;
  white-space: nowrap;
  font-size: 12px;
}
.data-table td { padding: 9px 8px; border-bottom: 1px solid #f5f5f5; color: #444; }
.row-even { background: #fafbfc; }
.data-table tbody tr:hover { background: #e6f4ff; }
code { background: #f0f5ff; padding: 2px 6px; border-radius: 3px; font-size: 12px; color: #1677ff; border: 1px solid #d6e4ff; }

.col-sn { width: 40px; text-align: center; }
.col-zh { min-width: 110px; }
.col-en { min-width: 140px; }
.col-iso { width: 60px; text-align: center; }
.col-num { width: 72px; text-align: center; }
.col-code { width: 72px; text-align: center; font-weight: 600; }
.col-domain { width: 72px; text-align: center; }
.col-remark { min-width: 140px; font-size: 12px; color: #888; }
.col-enRemark { min-width: 180px; font-size: 11px; color: #aaa; font-style: italic; }

@media (max-width: 768px) {
  .letter-group { scroll-margin-top: 135px; }
  .alpha-link { width: 28px; height: 28px; font-size: 12px; }
  .data-table { font-size: 12px; }
  .data-table th, .data-table td { padding: 6px 4px; }
}
</style>
